import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import { useHistory } from 'react-router';
import { useSelector } from 'react-redux';
import { Section } from '@/components/atoms/Section';
import { EmptyState } from '@/components/atoms/EmptyState';
import { Text } from '@/components/atoms/Text';
import { Checkbox } from '@/components/atoms/Checkbox';
import { SvyIcon } from '@/components/savory/SvyIcon';
import RouterNavigationTabs from '@/components/organisms/RouterNavigationTabs';
import { Routes } from '@/core/router/routes.types';
import { useRestaurantTabRoutes } from '../hooks/useRestaurantTabRoutes';
import { getUserToken } from '@/redux/user/user.slice';
import { useConfigureOperationZoneMutation } from '@/services/innovorder/operationZone/operationZone.endpoints';
import { useGetPosDevicesQuery } from '@/services/innovorder/posDevice/posDevice.endpoints';
import {
    useGetFiscalityProviderQuery,
    useUpsertFiscalityProviderMutation,
} from '@/services/innovorder/fiscalityProvider/fiscalityProvider.endpoints';
import { useOperationZonesVM } from './OperationZones.viewmodel';
import ConfigureZoneModal from './ConfigureZoneModal';
import type { ConfigureZoneFormValues } from './ConfigureZoneModal';
import { formatLegalAddressName, isZoneFullyConfigured } from './OperationZones.type';
import type { Territory } from './OperationZones.type';
import {
    ConfigureButton,
    ViewVatRatesButton,
    Container,
    Content,
    EmptyStateBox,
    FlagContainer,
    InfoField,
    InfoFieldValue,
    InfoFields,
    PrimaryBadge,
    PrimaryZoneBadge,
    PrimaryZoneCard,
    PrimaryZoneHeader,
    PrimaryZoneInfo,
    PrimaryZoneName,
    SecondaryZoneChip,
    SecondaryZoneChips,
    SecondaryZonesSection,
    SecondaryZonesTitle,
    SelectedCount,
    StarButton,
    TerritoryCode,
    TerritoryInfo,
    TerritoryMeta,
    TerritoryName,
    TerritoryRow,
    ConfiguredText,
    ToConfigureText,
    WarningBanner,
    WarningContent,
    WarningText,
    WarningTitle,
    InfoBanner,
    SupportLink,
} from './OperationZones.style';

const SUPPORT_MAIL = 'support@innovorder.fr';
const SUPPORT_SUBJECT = "Zone d'opération - Demande d'assistance";

const OperationZones: React.FunctionComponent<React.PropsWithChildren> = () => {
    const {
        territories,
        selectedTerritoryIds,
        selectedCount,
        toggleTerritory,
        primaryTerritoryId,
        primaryTerritory,
        secondaryTerritories,
        setPrimaryTerritory,
        hasSelectedTerritories,
        hasUnconfiguredZones,
        unconfiguredCount,
        configuredZonesMap,
        isMutating,
        isTerritoryDisabledByCurrency,
    } = useOperationZonesVM();
    const intl = useIntl();
    const history = useHistory();
    const { brandId, restaurantId } = useParams<{ brandId: string; restaurantId: string }>();
    const token = useSelector(getUserToken);
    const [configureOperationZone] = useConfigureOperationZoneMutation();
    const [upsertFiscalityProvider] = useUpsertFiscalityProviderMutation();

    const [configureTerritory, setConfigureTerritory] = React.useState<Territory | null>(null);

    const configuredZone = configureTerritory ? configuredZonesMap.get(configureTerritory.id) : undefined;

    const { data: posDevices } = useGetPosDevicesQuery(
        { brandId: Number(brandId), restaurantId: Number(restaurantId) },
        { skip: !brandId || !restaurantId },
    );

    const { data: fiscalityProviderData } = useGetFiscalityProviderQuery(
        {
            restaurantId: Number(restaurantId),
            operationZoneId: configuredZone?.operationZoneId ?? 0,
            token,
        },
        { skip: !configuredZone?.operationZoneId },
    );

    const devices = React.useMemo(() => {
        if (!posDevices) return [];
        return posDevices.map((d) => ({ deviceId: d.posDeviceId, deviceName: d.deviceName }));
    }, [posDevices]);

    const hasPosDevices = devices.length > 0;

    const handleOpenConfigure = React.useCallback((territory: Territory, e?: React.MouseEvent) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        setConfigureTerritory(territory);
    }, []);

    const handleCloseConfigure = React.useCallback(() => {
        setConfigureTerritory(null);
    }, []);

    const handleOpenVatRates = React.useCallback(
        (territory: Territory, e?: React.MouseEvent) => {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            history.push(`${Routes.VatRatesMatrix}?zone=${encodeURIComponent(territory.code)}`);
        },
        [history],
    );

    const handleSubmitConfigure = React.useCallback(
        async (values: ConfigureZoneFormValues, selectedProvider: string) => {
            if (!configureTerritory || !restaurantId) return;
            const zone = configuredZonesMap.get(configureTerritory.id);
            if (!zone) return;

            await configureOperationZone({
                restaurantId: Number(restaurantId),
                operationZoneId: zone.operationZoneId,
                token,
                socialReason: values.socialReason,
                address: { ...values.address, name: values.addressName },
                vatNumber: values.vatNumber,
                siret: values.siret,
                provider: selectedProvider,
                fiscalCode: values.fiscalRepresentative || null,
            }).unwrap();

            if (selectedProvider === 'FISKALTRUST' && values.deviceFiscalityConfigs) {
                await upsertFiscalityProvider({
                    restaurantId: Number(restaurantId),
                    operationZoneId: zone.operationZoneId,
                    token,
                    provider: 'FISKALTRUST',
                    fiscalCode: values.fiscalRepresentative || null,
                    deviceFiscalityConfigs: values.deviceFiscalityConfigs.map((config) => ({
                        ...config,
                        countryCode: configureTerritory.countryCode,
                    })),
                }).unwrap();
            }

            handleCloseConfigure();
        },
        [
            configureTerritory,
            restaurantId,
            configuredZonesMap,
            configureOperationZone,
            upsertFiscalityProvider,
            token,
            handleCloseConfigure,
        ],
    );

    const tabRoutes = useRestaurantTabRoutes();

    const renderSolution = (territory: Territory) => {
        if (territory.solution === 'operationZones.multiProvider') {
            return intl.formatMessage({ id: 'operationZones.multiProvider' });
        }
        return territory.solution;
    };

    return (
        <RouterNavigationTabs routes={tabRoutes}>
            <Container>
                <Content>
                    <Section title="operationZones.page.title" description="operationZones.page.description">
                        <InfoBanner>
                            <SvyIcon icon="information-line" />
                            <WarningContent>
                                <WarningText>
                                    <FormattedMessage
                                        id="operationZones.immutabilityNotice"
                                        values={{
                                            supportLink: ((chunks: React.ReactNode) => (
                                                <SupportLink
                                                    href={`mailto:${SUPPORT_MAIL}?subject=${encodeURIComponent(
                                                        SUPPORT_SUBJECT,
                                                    )}`}
                                                >
                                                    {chunks}
                                                </SupportLink>
                                            )) as unknown as React.ReactNode,
                                        }}
                                    />
                                </WarningText>
                            </WarningContent>
                        </InfoBanner>
                        {hasUnconfiguredZones && (
                            <WarningBanner>
                                <SvyIcon icon="error-warning-line" />
                                <WarningContent>
                                    <WarningTitle>
                                        <FormattedMessage id="operationZones.warning.title" />
                                    </WarningTitle>
                                    <WarningText>
                                        <FormattedMessage
                                            id="operationZones.warning.description"
                                            values={{ count: unconfiguredCount }}
                                        />
                                    </WarningText>
                                    <WarningText>
                                        <FormattedMessage id="operationZones.warning.hint" />
                                    </WarningText>
                                </WarningContent>
                            </WarningBanner>
                        )}

                        {primaryTerritory &&
                            (() => {
                                const primaryConfiguredZone = configuredZonesMap.get(primaryTerritory.id);
                                return (
                                    <PrimaryZoneCard>
                                        <FlagContainer size="large">{primaryTerritory.flag}</FlagContainer>
                                        <PrimaryZoneInfo>
                                            <PrimaryZoneHeader>
                                                <PrimaryZoneBadge>
                                                    <SvyIcon icon="star-fill" />
                                                    <FormattedMessage id="operationZones.primaryZone" />
                                                </PrimaryZoneBadge>
                                                <PrimaryZoneName>
                                                    {primaryConfiguredZone?.operationZone.name ?? primaryTerritory.name}{' '}
                                                    (
                                                    {primaryConfiguredZone?.operationZone.code ?? primaryTerritory.code}
                                                    )
                                                </PrimaryZoneName>
                                            </PrimaryZoneHeader>
                                            <InfoFields>
                                                <InfoField>
                                                    <SvyIcon icon="building-line" />
                                                    <FormattedMessage id="operationZones.entity" /> :{' '}
                                                    <InfoFieldValue>
                                                        {primaryConfiguredZone?.legal?.socialReason ?? (
                                                            <FormattedMessage id="operationZones.notFilled" />
                                                        )}
                                                    </InfoFieldValue>
                                                </InfoField>
                                                <InfoField>
                                                    <SvyIcon icon="file-text-line" />
                                                    <FormattedMessage id="operationZones.vat" /> :{' '}
                                                    <InfoFieldValue>
                                                        {primaryConfiguredZone?.legal?.taxNumber ?? (
                                                            <FormattedMessage id="operationZones.notFilled" />
                                                        )}
                                                    </InfoFieldValue>
                                                </InfoField>
                                                <InfoField>
                                                    <SvyIcon icon="computer-line" />
                                                    <FormattedMessage id="operationZones.posConfig" /> :{' '}
                                                    <InfoFieldValue>
                                                        {primaryConfiguredZone?.fiscalityProvider ? (
                                                            primaryConfiguredZone.fiscalityProvider.provider ===
                                                            'NF525' ? (
                                                                <FormattedMessage id="operationZones.initialized" />
                                                            ) : (
                                                                primaryConfiguredZone.fiscalityProvider.provider
                                                            )
                                                        ) : (
                                                            <FormattedMessage id="operationZones.pending" />
                                                        )}
                                                    </InfoFieldValue>
                                                </InfoField>
                                            </InfoFields>
                                            {primaryTerritory.availableProviders.includes('FISKALTRUST') &&
                                                !hasPosDevices && (
                                                    <ToConfigureText>
                                                        <SvyIcon icon="error-warning-line" />
                                                        <FormattedMessage id="operationZones.noPosDevice" />
                                                    </ToConfigureText>
                                                )}
                                        </PrimaryZoneInfo>
                                        <ConfigureButton
                                            onClick={(e) => handleOpenConfigure(primaryTerritory, e)}
                                            disabled={
                                                primaryTerritory.availableProviders.includes('FISKALTRUST') &&
                                                !hasPosDevices
                                            }
                                        >
                                            <SvyIcon icon="settings-4-line" />
                                            <FormattedMessage id="operationZones.configure" />
                                        </ConfigureButton>
                                        <ViewVatRatesButton onClick={(e) => handleOpenVatRates(primaryTerritory, e)}>
                                            <SvyIcon icon="file-text-line" />
                                            <FormattedMessage id="operationZones.viewVatRates" />
                                        </ViewVatRatesButton>
                                    </PrimaryZoneCard>
                                );
                            })()}

                        {secondaryTerritories.length > 0 && (
                            <SecondaryZonesSection>
                                <SecondaryZonesTitle>
                                    <FormattedMessage
                                        id="operationZones.secondaryZonesActive"
                                        values={{ count: secondaryTerritories.length }}
                                    />
                                </SecondaryZonesTitle>
                                <SecondaryZoneChips>
                                    {secondaryTerritories.map((territory) => (
                                        <SecondaryZoneChip key={territory.id}>
                                            {territory.flag} {territory.name} ({territory.code})
                                        </SecondaryZoneChip>
                                    ))}
                                </SecondaryZoneChips>
                            </SecondaryZonesSection>
                        )}

                        {!hasSelectedTerritories && (
                            <EmptyStateBox>
                                <EmptyState emptyMessageId="operationZones.emptyState" size="medium">
                                    <Text text="operationZones.emptyState.description" />
                                </EmptyState>
                            </EmptyStateBox>
                        )}
                    </Section>

                    <Section
                        title="operationZones.availableTerritories"
                        actions={
                            <SelectedCount>
                                <FormattedMessage id="operationZones.selectedCount" values={{ count: selectedCount }} />
                            </SelectedCount>
                        }
                    >
                        {territories.map((territory) => {
                            const isSelected = selectedTerritoryIds.includes(territory.id);
                            const isPrimary = territory.id === primaryTerritoryId;
                            const zoneDetail = configuredZonesMap.get(territory.id);
                            const isConfigured = zoneDetail ? isZoneFullyConfigured(zoneDetail) : false;
                            const isConfigureDisabled =
                                territory.availableProviders.includes('FISKALTRUST') && !hasPosDevices;
                            const isCurrencyDisabled = isTerritoryDisabledByCurrency(territory);

                            return (
                                <TerritoryRow
                                    key={territory.id}
                                    htmlFor={isCurrencyDisabled ? undefined : `territory-${territory.id}`}
                                    data-disabled={isCurrencyDisabled || undefined}
                                    title={
                                        isCurrencyDisabled
                                            ? intl.formatMessage({
                                                  id: 'brandRestaurants.create.operationZone.currencyMismatch',
                                              })
                                            : undefined
                                    }
                                >
                                    <Checkbox
                                        id={`territory-${territory.id}`}
                                        checked={isSelected}
                                        onChange={() => toggleTerritory(territory.id)}
                                        disabled={
                                            isMutating || (isSelected && selectedCount <= 1) || isCurrencyDisabled
                                        }
                                    />
                                    <FlagContainer>{territory.flag}</FlagContainer>
                                    <TerritoryInfo>
                                        <TerritoryName>
                                            {territory.name} <TerritoryCode>({territory.code})</TerritoryCode>
                                            {isPrimary && (
                                                <PrimaryBadge>
                                                    <SvyIcon icon="star-fill" />
                                                    <FormattedMessage id="operationZones.primaryBadge" />
                                                </PrimaryBadge>
                                            )}
                                        </TerritoryName>
                                        <TerritoryMeta>
                                            {intl.formatMessage({ id: 'operationZones.solution' })}:{' '}
                                            {renderSolution(territory)}
                                            {isSelected && (
                                                <>
                                                    {' · '}
                                                    {isConfigured ? (
                                                        <ConfiguredText>
                                                            <SvyIcon icon="check-line" />
                                                            <FormattedMessage id="operationZones.configured" />
                                                        </ConfiguredText>
                                                    ) : (
                                                        <ToConfigureText>
                                                            <SvyIcon icon="time-line" />
                                                            <FormattedMessage id="operationZones.toConfigure" />
                                                        </ToConfigureText>
                                                    )}
                                                    {isConfigureDisabled && (
                                                        <>
                                                            {' · '}
                                                            <ToConfigureText>
                                                                <SvyIcon icon="error-warning-line" />
                                                                <FormattedMessage id="operationZones.noPosDevice" />
                                                            </ToConfigureText>
                                                        </>
                                                    )}
                                                </>
                                            )}
                                        </TerritoryMeta>
                                    </TerritoryInfo>
                                    {isSelected && !isPrimary && (
                                        <StarButton
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                setPrimaryTerritory(territory.id);
                                            }}
                                            title={intl.formatMessage({
                                                id: isConfigured
                                                    ? 'operationZones.primaryZone'
                                                    : 'operationZones.setPrimary.disabled',
                                            })}
                                            disabled={isMutating || !isConfigured}
                                        >
                                            <SvyIcon icon="star-line" />
                                        </StarButton>
                                    )}
                                    {isSelected && (
                                        <>
                                            <ConfigureButton
                                                onClick={(e) => handleOpenConfigure(territory, e)}
                                                disabled={isConfigureDisabled}
                                            >
                                                <SvyIcon icon="settings-4-line" />
                                                <FormattedMessage id="operationZones.configure" />
                                            </ConfigureButton>
                                            <ViewVatRatesButton onClick={(e) => handleOpenVatRates(territory, e)}>
                                                <SvyIcon icon="file-text-line" />
                                                <FormattedMessage id="operationZones.viewVatRates" />
                                            </ViewVatRatesButton>
                                        </>
                                    )}
                                </TerritoryRow>
                            );
                        })}
                    </Section>
                </Content>
            </Container>

            {configureTerritory &&
                (() => {
                    const zone = configuredZonesMap.get(configureTerritory.id);
                    return (
                        <ConfigureZoneModal
                            isOpen={Boolean(configureTerritory)}
                            onHide={handleCloseConfigure}
                            territory={configureTerritory}
                            availableProviders={configureTerritory.availableProviders}
                            countryCode={configureTerritory.countryCode}
                            devices={devices}
                            existingDeviceFiscalityConfigs={fiscalityProviderData?.deviceFiscalityConfigs}
                            defaultValues={
                                zone
                                    ? (() => {
                                          const addr = zone.legal?.address;
                                          return {
                                              socialReason: zone.legal?.socialReason ?? '',
                                              addressName: formatLegalAddressName(addr),
                                              address: {
                                                  route: addr?.route ?? '',
                                                  postalCode: addr?.postalCode ?? '',
                                                  locality: addr?.locality ?? '',
                                                  country: addr?.country ?? '',
                                                  streetNumber: addr?.streetNumber ?? '',
                                                  administrativeAreaLevel1: addr?.administrativeAreaLevel1 ?? '',
                                                  administrativeAreaLevel2: addr?.administrativeAreaLevel2 ?? '',
                                                  provider: addr?.provider ?? '',
                                                  providerId: addr?.providerId ?? '',
                                                  lat: addr?.lat ?? 0,
                                                  lng: addr?.lng ?? 0,
                                              },
                                              vatNumber: zone.legal?.taxNumber ?? '',
                                              siret: zone.legal?.siren ?? '',
                                              fiscalRepresentative: zone.legal?.fiscalCode ?? '',
                                          };
                                      })()
                                    : undefined
                            }
                            onSubmit={handleSubmitConfigure}
                        />
                    );
                })()}
        </RouterNavigationTabs>
    );
};

export default OperationZones;
